import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Phone, Mail, MapPin, Building2, Send } from "lucide-react";
import FacilityChat from "@/components/FacilityChat";
import FacilityEmailDialog from "@/components/FacilityEmailDialog";
import FacilityProviders from "@/components/FacilityProviders";
import FacilityMarketing from "@/components/FacilityMarketing";
import FacilityRiskCard from "@/components/FacilityRiskCard";
import CoverageHeatmap from "@/components/CoverageHeatmap";
import HealthScoreChip from "@/components/HealthScoreChip";
import HealthTrend from "@/components/HealthTrend";

export default function FacilityDetail() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const [visits, setVisits] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [swots, setSwots] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [health, setHealth] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    api.get(`/facilities/${id}`).then(({ data }) => setF(data));
    api.get("/facilities/health-scores").then(({ data }) => setHealth(data[id] || null)).catch(() => {});
    api.get("/facilities/health-history", { params: { days: 90, facility_id: id } }).then(({ data }) => setTrend(data[id] || [])).catch(() => {});
    api.get("/visits", { params: { facility_id: id } }).then(({ data }) => setVisits(data));
    api.get("/followups", { params: { facility_id: id } }).then(({ data }) => setFollowups(data));
    api.get("/swots", { params: { facility_id: id } }).then(({ data }) => setSwots(data));
  }, [id]);

  if (!f) return <div className="p-8 text-slate-500 font-mono-plex text-sm">Loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="facility-detail">
      <Link to="/facilities" className="text-xs text-slate-500 font-mono-plex hover:text-slate-900 inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> All facilities</Link>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap"><Badge variant="outline">{f.type}</Badge><Badge>{f.referral_status.replace("_", " ")}</Badge>{f.at_risk && <Badge className="bg-rose-600 hover:bg-rose-600" data-testid="facility-at-risk-badge">At risk</Badge>}<HealthScoreChip score={health} size="lg" /><HealthTrend series={trend} /></div>
          <h1 className="font-heading text-4xl font-black">{f.name}</h1>
          <div className="text-slate-500 text-sm mt-1 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {f.address}, {f.city}, {f.county}</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setEmailOpen(true)} disabled={!f.admin_contact_email} data-testid="facility-email-btn" className="bg-slate-900 hover:bg-slate-800">
            <Send className="w-4 h-4 mr-1" /> Email contact
          </Button>
        </div>
      </div>
      <FacilityEmailDialog facility={f} open={emailOpen} onOpenChange={setEmailOpen} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="p-5 border-slate-200 shadow-none">
          <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 mb-2">Capacity</div>
          <div className="font-heading text-3xl font-bold">{f.census ?? "—"}<span className="text-slate-400 text-xl">/{f.capacity ?? "—"}</span></div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 mb-2">Payer Mix</div>
          <div className="text-sm text-slate-900 font-mono-plex">{f.payer_mix || "—"}</div>
          <div className="text-xs text-slate-500 mt-2">Medicaid ID: <span className="font-mono-plex">{f.medicaid_id || "—"}</span></div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-none">
          <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 mb-2">Corporate Owner</div>
          <div className="text-sm font-medium flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {f.corporate_owner_name || "Independent"}</div>
          {f.corporate_owner_id && <Link className="text-xs text-slate-500 hover:text-slate-900 mt-1 inline-block" to={`/owners/${f.corporate_owner_id}`}>View owner →</Link>}
        </Card>
      </div>

      <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4"><FacilityRiskCard facility={f} onChange={setF} /><CoverageHeatmap facility={f} /></div>

      <Card className="p-6 border-slate-200 shadow-none mb-6">
        <h3 className="font-heading font-bold text-xl mb-3">Admin contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div><div className="text-xs text-slate-500 uppercase tracking-widest font-mono-plex mb-1">Name</div>{f.admin_contact_name || "—"}</div>
          <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" />{f.admin_contact_phone || "—"}</div>
          <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" />{f.admin_contact_email || "—"}</div>
        </div>
        {f.notes && <div className="mt-4 text-sm text-slate-700 whitespace-pre-wrap">{f.notes}</div>}
      </Card>

      <Tabs defaultValue="visits">
        <TabsList>
          <TabsTrigger value="visits" data-testid="tab-visits">Visits ({visits.length})</TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups">Follow-ups ({followups.length})</TabsTrigger>
          <TabsTrigger value="swots" data-testid="tab-swots">SWOT ({swots.length})</TabsTrigger>
          <TabsTrigger value="providers" data-testid="tab-providers">Providers</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="tab-marketing">Marketing</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat">AI Chat</TabsTrigger>
        </TabsList>
        <TabsContent value="visits">
          <Card className="border-slate-200 shadow-none">
            {visits.length === 0 ? <div className="p-6 text-slate-400 text-sm">No visits yet. <Link className="underline" to="/visits">Log a visit →</Link></div> :
              <div className="divide-y divide-slate-100">
                {visits.map(v => (
                  <div key={v.visit_id} className="p-4 flex justify-between gap-3">
                    <div><div className="text-sm font-medium">{v.purpose}</div><div className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{v.notes}</div></div>
                    <div className="text-right text-xs"><div className="font-mono-plex">{v.visit_date}</div><div className="text-slate-500">{v.created_by_name}</div></div>
                  </div>
                ))}
              </div>}
          </Card>
        </TabsContent>
        <TabsContent value="followups">
          <Card className="border-slate-200 shadow-none">
            {followups.length === 0 ? <div className="p-6 text-slate-400 text-sm">No follow-ups. <Link className="underline" to="/followups">Add one →</Link></div> :
              <div className="divide-y divide-slate-100">
                {followups.map(fu => (
                  <div key={fu.followup_id} className="p-4 flex justify-between gap-3">
                    <div><div className="text-sm font-medium">{fu.title}</div><div className="text-xs text-slate-500 mt-0.5">{fu.notes}</div></div>
                    <div className="text-right text-xs"><div className="font-mono-plex">{fu.due_date}</div><Badge variant="outline" className="mt-1">{fu.status}</Badge></div>
                  </div>
                ))}
              </div>}
          </Card>
        </TabsContent>
        <TabsContent value="swots">
          <Card className="border-slate-200 shadow-none">
            {swots.length === 0 ? <div className="p-6 text-slate-400 text-sm">No SWOTs yet. <Link className="underline" to="/swots">Create one →</Link></div> :
              <div className="divide-y divide-slate-100">
                {swots.map(s => (
                  <div key={s.swot_id} className="p-4 flex justify-between gap-3">
                    <div className="text-sm">SWOT · {s.strengths.length + s.weaknesses.length + s.opportunities.length + s.threats.length} items {s.ai_generated && <Badge variant="outline" className="ml-2 text-[10px]">AI</Badge>}</div>
                    <div className="text-xs text-slate-500 font-mono-plex">{(s.updated_at || "").slice(0, 10)}</div>
                  </div>
                ))}
              </div>}
          </Card>
        </TabsContent>
        <TabsContent value="providers">
          <FacilityProviders facility={f} />
        </TabsContent>
        <TabsContent value="marketing">
          <FacilityMarketing facility={f} />
        </TabsContent>
        <TabsContent value="chat">
          <FacilityChat facilityId={f.facility_id} facilityName={f.name} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
